import { Injectable, computed, effect, signal } from '@angular/core';
import { Observable, asyncScheduler, firstValueFrom, scheduled, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, tap } from 'rxjs/operators';
import {
  STUDIO_INSERT_PRESETS,
  StudioComponentDefinition,
  StudioComponentStatus,
  StudioInsertPreset,
  StudioNode,
  StudioNodeAnimation,
  StudioNodeContent,
  StudioNodeStyle,
  StudioNodeTransform,
  collectStudioDropListIds,
  createDefaultStudioNodeStyle,
  createStudioId,
  findStudioNode,
  findStudioParentNode,
  isStudioContainerNode,
  normalizeStudioNodeTree
} from '../models/component-studio.models';
import { ComponentStudioApiError, ComponentStudioApiService } from './component-studio-api.service';
import { UndoRedoHistory } from '../utils/undo-redo-history';

interface ComponentStudioHistorySnapshot {
  customDefinitions: StudioComponentDefinition[];
  activeDefinitionId: string | null;
  selectedNodeId: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class ComponentStudioService {
  private readonly legacyDefinitionsStorageKey = 'bw.component-studio.definitions';
  private readonly pendingDefinitionsStorageKey = 'bw.component-studio.pending-definitions';
  private readonly saveIdleDelayMs = 10000;
  private readonly saveMaxDelayMs = 60000;
  private readonly builtInDefinitions = createStarterDefinitions();
  private readonly definitionsSignal = signal<StudioComponentDefinition[]>(this.builtInDefinitions);
  private readonly activeDefinitionIdSignal = signal<string | null>(null);
  private readonly selectedNodeIdSignal = signal<string | null>(null);
  private readonly loadedSignal = signal(false);
  private readonly loadingSignal = signal(false);
  private readonly loadErrorSignal = signal<string | null>(null);
  private readonly savingSignal = signal(false);
  private readonly saveErrorSignal = signal<string | null>(null);
  private readonly canUndoSignal = signal(false);
  private readonly canRedoSignal = signal(false);
  private readonly hasPendingChangesSignal = signal(false);
  private loadRequest$: Observable<StudioComponentDefinition[]> | null = null;
  private readonly history = new UndoRedoHistory<ComponentStudioHistorySnapshot>(
    (snapshot) => JSON.stringify(snapshot)
  );
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private maxSaveTimer: ReturnType<typeof setTimeout> | null = null;
  private dirtySince: number | null = null;
  private lastSavedSerialized = '[]';
  private savePromise: Promise<void> | null = null;

  readonly definitions = computed(() => this.definitionsSignal());
  readonly activeDefinitionId = computed(() => this.activeDefinitionIdSignal());
  readonly activeDefinition = computed<StudioComponentDefinition | null>(() => {
    const definitions = this.definitionsSignal();
    const activeId = this.activeDefinitionIdSignal();
    const activeDefinition = definitions.find((definition) => definition.id === activeId);

    return activeDefinition ?? definitions[0] ?? null;
  });
  readonly selectedNodeId = computed(() => this.selectedNodeIdSignal());
  readonly selectedNode = computed<StudioNode | null>(() => {
    const activeDefinition = this.activeDefinition();
    if (!activeDefinition) {
      return null;
    }

    const selectedNodeId = this.selectedNodeIdSignal() ?? activeDefinition.root.id;
    return findStudioNode(activeDefinition.root, selectedNodeId) ?? activeDefinition.root;
  });
  readonly selectedParentNode = computed<StudioNode | null>(() => {
    const activeDefinition = this.activeDefinition();
    const selectedNode = this.selectedNode();

    if (!activeDefinition || !selectedNode) {
      return null;
    }

    return findStudioParentNode(activeDefinition.root, selectedNode.id);
  });
  readonly dropListIds = computed(() => {
    const activeDefinition = this.activeDefinition();
    return activeDefinition ? collectStudioDropListIds(activeDefinition.root) : [];
  });
  readonly presets = STUDIO_INSERT_PRESETS;
  readonly isLoaded = computed(() => this.loadedSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly loadError = computed(() => this.loadErrorSignal());
  readonly isSaving = computed(() => this.savingSignal());
  readonly saveError = computed(() => this.saveErrorSignal());
  readonly canUndo = computed(() => this.canUndoSignal());
  readonly canRedo = computed(() => this.canRedoSignal());
  readonly hasPendingChanges = computed(() => this.hasPendingChangesSignal());
  readonly stats = computed(() => {
    const definitions = this.definitionsSignal();
    return {
      total: definitions.length,
      builtIn: definitions.filter((definition) => definition.source === 'built-in').length,
      custom: definitions.filter((definition) => definition.source === 'custom').length,
      ready: definitions.filter((definition) => definition.status === 'ready').length,
      draft: definitions.filter((definition) => definition.status === 'draft').length,
      totalUsage: definitions.reduce((sum, definition) => sum + definition.usageCount, 0)
    };
  });

  constructor(private readonly componentStudioApiService: ComponentStudioApiService) {
    effect(() => {
      const activeDefinition = this.activeDefinition();
      if (!activeDefinition) {
        this.selectedNodeIdSignal.set(null);
        return;
      }

      const selectedNodeId = this.selectedNodeIdSignal();
      if (!selectedNodeId || !findStudioNode(activeDefinition.root, selectedNodeId)) {
        this.selectedNodeIdSignal.set(activeDefinition.root.id);
      }
    });
  }

  loadState(force = false): Observable<StudioComponentDefinition[]> {
    if (this.loadedSignal() && !force) {
      return scheduled([this.getCustomDefinitions()], asyncScheduler);
    }

    if (this.loadRequest$ && !force) {
      return this.loadRequest$;
    }

    this.loadingSignal.set(true);
    this.loadErrorSignal.set(null);

    const request$ = this.componentStudioApiService.getState().pipe(
      tap((customDefinitions) => {
        const mergedDefinitions = this.mergeLegacyCustomDefinitions(customDefinitions);
        this.replaceCustomDefinitions(mergedDefinitions);
        this.loadedSignal.set(true);
        this.lastSavedSerialized = this.serializePersistedDefinitions(this.getCustomDefinitions());

        const restoredPendingDraft = this.restorePendingDraft();
        if (!restoredPendingDraft && mergedDefinitions.length !== customDefinitions.length) {
          this.markStateChanged();
        } else if (!restoredPendingDraft) {
          this.clearPendingDraftStorage();
          this.hasPendingChangesSignal.set(false);
          this.clearScheduledSave();
          this.dirtySince = null;
        }

        this.history.clear();
        this.syncHistorySignals();
      }),
      catchError((error) => {
        this.loadErrorSignal.set(this.resolveErrorMessage(error));
        return throwError(() => error);
      }),
      finalize(() => {
        this.loadingSignal.set(false);
        this.loadRequest$ = null;
      }),
      shareReplay(1)
    );

    this.loadRequest$ = request$;
    return request$;
  }

  setActiveDefinition(definitionId: string): void {
    this.activeDefinitionIdSignal.set(definitionId);
  }

  selectNode(nodeId: string | null): void {
    const activeDefinition = this.activeDefinition();
    if (!activeDefinition) {
      this.selectedNodeIdSignal.set(null);
      return;
    }

    if (!nodeId || !findStudioNode(activeDefinition.root, nodeId)) {
      this.selectedNodeIdSignal.set(activeDefinition.root.id);
      return;
    }

    this.selectedNodeIdSignal.set(nodeId);
  }

  createBlankDefinition(name = 'Untitled component'): string {
    this.captureHistorySnapshot();
    const definition = createBlankComponentDefinition(name);

    this.definitionsSignal.update((definitions) => [definition, ...definitions]);
    this.activeDefinitionIdSignal.set(definition.id);
    this.selectedNodeIdSignal.set(definition.root.id);
    this.markStateChanged();

    return definition.id;
  }

  registerExternalDefinition(definition: StudioComponentDefinition): string {
    this.captureHistorySnapshot();
    const normalizedDefinition = normalizeDefinition({
      ...structuredClone(definition),
      source: 'custom',
      updatedAt: new Date().toISOString()
    });

    this.definitionsSignal.update((definitions) => [
      normalizedDefinition,
      ...definitions.filter((entry) => entry.id !== normalizedDefinition.id)
    ]);
    this.activeDefinitionIdSignal.set(normalizedDefinition.id);
    this.selectedNodeIdSignal.set(normalizedDefinition.root.id);
    this.markStateChanged();

    return normalizedDefinition.id;
  }

  duplicateDefinition(definitionId: string): string | null {
    const sourceDefinition = this.definitionsSignal().find((definition) => definition.id === definitionId);
    if (!sourceDefinition) {
      return null;
    }

    this.captureHistorySnapshot();
    const duplicate = structuredClone(sourceDefinition);
    duplicate.id = createStudioId('component');
    duplicate.slug = `${sourceDefinition.slug}-copy`;
    duplicate.name = `${sourceDefinition.name} copy`;
    duplicate.source = 'custom';
    duplicate.version = 1;
    duplicate.status = 'draft';
    duplicate.updatedAt = new Date().toISOString();
    duplicate.usageCount = 0;
    duplicate.root = cloneNodeWithFreshIds(sourceDefinition.root);

    this.definitionsSignal.update((definitions) => [duplicate, ...definitions]);
    this.activeDefinitionIdSignal.set(duplicate.id);
    this.selectedNodeIdSignal.set(duplicate.root.id);
    this.markStateChanged();

    return duplicate.id;
  }

  deleteDefinition(definitionId: string): string | null {
    const sourceDefinition = this.definitionsSignal().find((definition) => definition.id === definitionId);
    if (!sourceDefinition || sourceDefinition.source !== 'custom') {
      return null;
    }

    this.captureHistorySnapshot();
    const remainingDefinitions = this.definitionsSignal().filter((definition) => definition.id !== definitionId);
    const nextDefinitionId = remainingDefinitions[0]?.id ?? null;

    this.definitionsSignal.set(remainingDefinitions);
    this.activeDefinitionIdSignal.set(nextDefinitionId);
    this.selectedNodeIdSignal.set(null);
    this.markStateChanged();

    return nextDefinitionId;
  }

  canDeleteDefinition(definition: StudioComponentDefinition | null | undefined): boolean {
    return !!definition && definition.source === 'custom';
  }

  renameActiveDefinition(name: string): void {
    this.patchActiveDefinition((definition) => {
      definition.name = name.trim() || 'Untitled component';
      definition.slug = slugify(definition.name);
    });
  }

  updateActiveDefinitionDescription(description: string): void {
    this.patchActiveDefinition((definition) => {
      definition.description = description;
    });
  }

  updateActiveDefinitionStatus(status: StudioComponentStatus): void {
    this.patchActiveDefinition((definition) => {
      definition.status = status;
    });
  }

  saveActiveDefinitionVersion(): void {
    this.patchActiveDefinition((definition) => {
      definition.version += 1;
    });
  }

  updateSelectedNodeLabel(label: string): void {
    this.patchSelectedNode((node) => {
      node.label = label.trim() || node.label;
    });
  }

  updateSelectedNodeContent(contentPatch: Partial<StudioNodeContent>): void {
    this.patchSelectedNode((node) => {
      node.content = {
        ...node.content,
        ...contentPatch
      };
    });
  }

  updateSelectedNodeStyle(stylePatch: Partial<StudioNodeStyle>): void {
    this.patchSelectedNode((node) => {
      node.style = {
        ...node.style,
        ...stylePatch
      };
    });
  }

  updateSelectedNodeTransform(transformPatch: Partial<StudioNodeTransform>): void {
    this.patchSelectedNode((node) => {
      node.style = {
        ...node.style,
        transform: {
          ...node.style.transform,
          ...transformPatch
        }
      };
    });
  }

  updateSelectedNodeAnimation(animationPatch: Partial<StudioNodeAnimation>): void {
    this.patchSelectedNode((node) => {
      node.style = {
        ...node.style,
        animation: {
          ...node.style.animation,
          ...animationPatch
        }
      };
    });
  }

  toggleSelectedHidden(): void {
    this.patchSelectedNode((node) => {
      node.hidden = !node.hidden;
    });
  }

  toggleSelectedLocked(): void {
    this.patchSelectedNode((node) => {
      node.locked = !node.locked;
    });
  }

  duplicateSelectedNode(): void {
    const activeDefinition = this.activeDefinition();
    const selectedNode = this.selectedNode();
    const selectedParent = this.selectedParentNode();

    if (!activeDefinition || !selectedNode || !selectedParent) {
      return;
    }

    const duplicateNode = cloneNodeWithFreshIds(selectedNode);

    this.patchActiveDefinition((definition) => {
      const duplicateParent = findStudioNode(definition.root, selectedParent.id);
      if (!duplicateParent) {
        return;
      }

      const insertIndex = duplicateParent.children.findIndex((child) => child.id === selectedNode.id);
      duplicateParent.children.splice(insertIndex + 1, 0, duplicateNode);
    });

    this.selectedNodeIdSignal.set(duplicateNode.id);
  }

  removeSelectedNode(): void {
    const activeDefinition = this.activeDefinition();
    const selectedNode = this.selectedNode();
    const selectedParent = this.selectedParentNode();

    if (!activeDefinition || !selectedNode || !selectedParent) {
      return;
    }

    this.patchActiveDefinition((definition) => {
      const parent = findStudioNode(definition.root, selectedParent.id);
      if (!parent) {
        return;
      }

      parent.children = parent.children.filter((child) => child.id !== selectedNode.id);
    });

    this.selectedNodeIdSignal.set(selectedParent.id);
  }

  insertPreset(presetId: string, explicitParentId?: string | null): string | null {
    const activeDefinition = this.activeDefinition();
    if (!activeDefinition) {
      return null;
    }

    const targetParentId = explicitParentId ?? this.resolveInsertionParentId(activeDefinition.root);
    const targetParent = findStudioNode(activeDefinition.root, targetParentId);
    if (!targetParent || !isStudioContainerNode(targetParent)) {
      return null;
    }

    const newNode = buildNodeFromPreset(presetId);

    this.patchActiveDefinition((definition) => {
      const parent = findStudioNode(definition.root, targetParentId);
      if (!parent) {
        return;
      }

      parent.children.push(newNode);
    });

    this.selectedNodeIdSignal.set(newNode.id);
    return newNode.id;
  }

  moveNode(nodeId: string, targetParentId: string, targetIndex: number): void {
    const activeDefinition = this.activeDefinition();
    if (!activeDefinition || activeDefinition.root.id === nodeId) {
      return;
    }

    const sourceParent = findStudioParentNode(activeDefinition.root, nodeId);
    const targetParent = findStudioNode(activeDefinition.root, targetParentId);
    const draggedNode = findStudioNode(activeDefinition.root, nodeId);
    if (!sourceParent || !targetParent || !draggedNode || !isStudioContainerNode(targetParent)) {
      return;
    }

    if (draggedNode.id === targetParentId || findStudioNode(draggedNode, targetParentId)) {
      return;
    }

    this.patchActiveDefinition((definition) => {
      const draftSourceParent = findStudioParentNode(definition.root, nodeId);
      const draftTargetParent = findStudioNode(definition.root, targetParentId);
      if (!draftSourceParent || !draftTargetParent || !isStudioContainerNode(draftTargetParent)) {
        return;
      }

      const sourceIndex = draftSourceParent.children.findIndex((child) => child.id === nodeId);
      if (sourceIndex === -1) {
        return;
      }

      const [movedNode] = draftSourceParent.children.splice(sourceIndex, 1);
      const clampedIndex = Math.max(0, Math.min(targetIndex, draftTargetParent.children.length));
      draftTargetParent.children.splice(clampedIndex, 0, movedNode);
    });
  }

  getPresetById(presetId: string): StudioInsertPreset | undefined {
    return STUDIO_INSERT_PRESETS.find((preset) => preset.id === presetId);
  }

  undo(): boolean {
    const snapshot = this.history.undo(this.captureStateSnapshot());
    if (!snapshot) {
      return false;
    }

    this.applyStateSnapshot(snapshot);
    this.syncHistorySignals();
    this.markStateChanged(false);
    return true;
  }

  redo(): boolean {
    const snapshot = this.history.redo(this.captureStateSnapshot());
    if (!snapshot) {
      return false;
    }

    this.applyStateSnapshot(snapshot);
    this.syncHistorySignals();
    this.markStateChanged(false);
    return true;
  }

  async flushPendingSave(): Promise<void> {
    if (!this.loadedSignal()) {
      return;
    }

    this.clearScheduledSave();

    while (true) {
      if (this.savePromise) {
        await this.savePromise;
        continue;
      }

      if (!this.hasPersistedStateChanges()) {
        this.hasPendingChangesSignal.set(false);
        this.clearPendingDraftStorage();
        this.dirtySince = null;
        return;
      }

      await this.performRemoteSave();
    }
  }

  flushPendingSaveOnUnload(): void {
    if (!this.loadedSignal() || !this.hasPersistedStateChanges()) {
      return;
    }

    this.persistPendingDraft();
    this.componentStudioApiService.saveStateKeepalive(this.getCustomDefinitions());
  }

  private patchSelectedNode(mutateNode: (node: StudioNode) => void): void {
    const selectedNode = this.selectedNode();
    if (!selectedNode) {
      return;
    }

    this.patchActiveDefinition((definition) => {
      const targetNode = findStudioNode(definition.root, selectedNode.id);
      if (!targetNode) {
        return;
      }

      mutateNode(targetNode);
    });
  }

  private patchActiveDefinition(mutateDefinition: (definition: StudioComponentDefinition) => void): void {
    let activeDefinition = this.activeDefinition();
    if (!activeDefinition) {
      return;
    }

    if (activeDefinition.source === 'built-in') {
      const duplicatedId = this.duplicateDefinition(activeDefinition.id);
      if (!duplicatedId) {
        return;
      }

      activeDefinition = this.definitionsSignal().find((definition) => definition.id === duplicatedId) ?? null;
      if (!activeDefinition) {
        return;
      }
    }

    const draftDefinition = structuredClone(activeDefinition);
    mutateDefinition(draftDefinition);
    if (JSON.stringify(draftDefinition) === JSON.stringify(activeDefinition)) {
      return;
    }

    this.captureHistorySnapshot();
    draftDefinition.updatedAt = new Date().toISOString();

    this.definitionsSignal.update((definitions) => definitions.map((definition) => (
      definition.id === draftDefinition.id ? draftDefinition : definition
    )));
    this.markStateChanged();
  }

  private resolveInsertionParentId(rootNode: StudioNode): string {
    const selectedNode = this.selectedNode();
    if (!selectedNode) {
      return rootNode.id;
    }

    if (isStudioContainerNode(selectedNode)) {
      return selectedNode.id;
    }

    return findStudioParentNode(rootNode, selectedNode.id)?.id ?? rootNode.id;
  }

  private replaceCustomDefinitions(customDefinitions: StudioComponentDefinition[]): void {
    const normalizedCustomDefinitions = customDefinitions.map((definition) => normalizeDefinition({
      ...definition,
      source: 'custom'
    }));

    this.definitionsSignal.set([...this.builtInDefinitions, ...normalizedCustomDefinitions]);
  }

  private getCustomDefinitions(): StudioComponentDefinition[] {
    return this.definitionsSignal()
      .filter((definition) => definition.source === 'custom')
      .map((definition) => structuredClone(definition));
  }

  private mergeLegacyCustomDefinitions(remoteDefinitions: StudioComponentDefinition[]): StudioComponentDefinition[] {
    if (typeof window === 'undefined') {
      return remoteDefinitions;
    }

    const legacyDefinitions = this.loadLegacyCustomDefinitions();
    if (!legacyDefinitions.length) {
      return remoteDefinitions;
    }

    const mergedDefinitions = new Map<string, StudioComponentDefinition>();
    remoteDefinitions.forEach((definition) => mergedDefinitions.set(definition.id, definition));
    legacyDefinitions.forEach((definition) => mergedDefinitions.set(definition.id, definition));

    window.localStorage.removeItem(this.legacyDefinitionsStorageKey);
    return Array.from(mergedDefinitions.values());
  }

  private loadLegacyCustomDefinitions(): StudioComponentDefinition[] {
    if (typeof window === 'undefined') {
      return [];
    }

    try {
      const storedDefinitions = window.localStorage.getItem(this.legacyDefinitionsStorageKey);
      if (!storedDefinitions) {
        return [];
      }

      const parsedDefinitions = JSON.parse(storedDefinitions) as StudioComponentDefinition[];
      if (!Array.isArray(parsedDefinitions)) {
        return [];
      }

      return parsedDefinitions
        .map((definition) => normalizeDefinition(definition))
        .filter((definition) => definition.source === 'custom');
    } catch {
      return [];
    }
  }

  private resolveErrorMessage(error: unknown): string {
    const apiError = error as ComponentStudioApiError | undefined;
    return apiError?.message || 'An unexpected error occurred';
  }

  private captureHistorySnapshot(): void {
    this.history.capture(this.captureStateSnapshot());
    this.syncHistorySignals();
  }

  private captureStateSnapshot(): ComponentStudioHistorySnapshot {
    return {
      customDefinitions: this.getCustomDefinitions(),
      activeDefinitionId: this.activeDefinitionIdSignal(),
      selectedNodeId: this.selectedNodeIdSignal()
    };
  }

  private applyStateSnapshot(snapshot: ComponentStudioHistorySnapshot): void {
    this.replaceCustomDefinitions(snapshot.customDefinitions);

    const nextActiveDefinitionId = this.definitionsSignal().some((definition) => definition.id === snapshot.activeDefinitionId)
      ? snapshot.activeDefinitionId
      : this.definitionsSignal()[0]?.id ?? null;

    this.activeDefinitionIdSignal.set(nextActiveDefinitionId);

    const activeDefinition = this.definitionsSignal().find((definition) => definition.id === nextActiveDefinitionId) ?? null;
    const nextSelectedNodeId = snapshot.selectedNodeId && activeDefinition
      && findStudioNode(activeDefinition.root, snapshot.selectedNodeId)
      ? snapshot.selectedNodeId
      : activeDefinition?.root.id ?? null;

    this.selectedNodeIdSignal.set(nextSelectedNodeId);
  }

  private markStateChanged(captureHistory = false): void {
    if (!this.loadedSignal()) {
      return;
    }

    if (captureHistory) {
      this.captureHistorySnapshot();
    }

    if (!this.hasPersistedStateChanges()) {
      this.hasPendingChangesSignal.set(false);
      this.clearPendingDraftStorage();
      this.clearScheduledSave();
      this.dirtySince = null;
      return;
    }

    this.hasPendingChangesSignal.set(true);
    this.persistPendingDraft();
    this.scheduleBufferedSave();
  }

  private scheduleBufferedSave(): void {
    if (!this.loadedSignal() || !this.hasPersistedStateChanges()) {
      return;
    }

    if (this.dirtySince === null) {
      this.dirtySince = Date.now();
    }

    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
    }

    this.saveTimer = setTimeout(() => {
      void this.performRemoteSave();
    }, this.saveIdleDelayMs);

    if (!this.maxSaveTimer && this.dirtySince !== null) {
      const elapsed = Date.now() - this.dirtySince;
      const remaining = Math.max(0, this.saveMaxDelayMs - elapsed);
      this.maxSaveTimer = setTimeout(() => {
        void this.performRemoteSave();
      }, remaining);
    }
  }

  private clearScheduledSave(): void {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }

    if (this.maxSaveTimer) {
      clearTimeout(this.maxSaveTimer);
      this.maxSaveTimer = null;
    }
  }

  private async performRemoteSave(): Promise<void> {
    if (!this.loadedSignal() || !this.hasPersistedStateChanges()) {
      return;
    }

    if (this.savePromise) {
      return this.savePromise;
    }

    this.clearScheduledSave();
    this.savingSignal.set(true);
    this.saveErrorSignal.set(null);

    const payload = this.getCustomDefinitions();
    const requestSerialized = this.serializePersistedDefinitions(payload);

    this.savePromise = firstValueFrom(this.componentStudioApiService.saveState(payload))
      .then((customDefinitions) => {
        const currentSerialized = this.serializePersistedDefinitions();
        const savedSerialized = this.serializePersistedDefinitions(customDefinitions);
        const shouldApplySavedState = currentSerialized === requestSerialized;

        if (shouldApplySavedState) {
          this.replaceCustomDefinitions(customDefinitions);
        }

        this.lastSavedSerialized = shouldApplySavedState
          ? this.serializePersistedDefinitions()
          : savedSerialized;

        if (this.hasPersistedStateChanges()) {
          this.hasPendingChangesSignal.set(true);
          this.persistPendingDraft();
          this.dirtySince = Date.now();
          this.scheduleBufferedSave();
          return;
        }

        this.hasPendingChangesSignal.set(false);
        this.clearPendingDraftStorage();
        this.dirtySince = null;
      })
      .catch((error: unknown) => {
        this.saveErrorSignal.set(this.resolveErrorMessage(error));
        this.hasPendingChangesSignal.set(this.hasPersistedStateChanges());
        this.persistPendingDraft();
        this.dirtySince ??= Date.now();
        this.scheduleBufferedSave();
      })
      .finally(() => {
        this.savingSignal.set(false);
        this.savePromise = null;
      });

    return this.savePromise;
  }

  private hasPersistedStateChanges(): boolean {
    return this.serializePersistedDefinitions() !== this.lastSavedSerialized;
  }

  private serializePersistedDefinitions(definitions = this.getCustomDefinitions()): string {
    return JSON.stringify(this.buildComparableDefinitions(definitions));
  }

  private buildComparableDefinitions(
    definitions: StudioComponentDefinition[]
  ): Array<Omit<StudioComponentDefinition, 'updatedAt'>> {
    return definitions.map((definition) => {
      const normalizedDefinition = normalizeDefinition({
        ...definition,
        source: 'custom'
      });
      const { updatedAt: _updatedAt, ...comparableDefinition } = normalizedDefinition;

      return comparableDefinition;
    });
  }

  private restorePendingDraft(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      const rawDraft = window.localStorage.getItem(this.pendingDefinitionsStorageKey);
      if (!rawDraft) {
        return false;
      }

      const parsedDraft = JSON.parse(rawDraft) as ComponentStudioHistorySnapshot;
      if (!Array.isArray(parsedDraft.customDefinitions)) {
        return false;
      }

      this.applyStateSnapshot({
        customDefinitions: parsedDraft.customDefinitions.map((definition) => normalizeDefinition({
          ...definition,
          source: 'custom'
        })),
        activeDefinitionId: parsedDraft.activeDefinitionId ?? null,
        selectedNodeId: parsedDraft.selectedNodeId ?? null
      });

      if (!this.hasPersistedStateChanges()) {
        this.clearPendingDraftStorage();
        return false;
      }

      this.hasPendingChangesSignal.set(true);
      this.dirtySince = Date.now();
      this.scheduleBufferedSave();
      return true;
    } catch {
      return false;
    }
  }

  private persistPendingDraft(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(this.pendingDefinitionsStorageKey, JSON.stringify(this.captureStateSnapshot()));
  }

  private clearPendingDraftStorage(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.removeItem(this.pendingDefinitionsStorageKey);
  }

  private syncHistorySignals(): void {
    this.canUndoSignal.set(this.history.canUndo);
    this.canRedoSignal.set(this.history.canRedo);
  }
}

function createStarterDefinitions(): StudioComponentDefinition[] {
  return [
    createHeroSpotlightDefinition(),
    createPricingMatrixDefinition(),
    createSocialProofDefinition()
  ];
}

function createHeroSpotlightDefinition(): StudioComponentDefinition {
  return {
    id: createStudioId('component'),
    slug: 'hero-spotlight',
    name: 'Hero spotlight',
    source: 'built-in',
    category: 'marketing',
    description: 'A conversion-first hero with a two-column spotlight, headline stack, and media proof block.',
    tags: ['hero', 'landing', 'conversion'],
    version: 4,
    status: 'ready',
    usageCount: 18,
    updatedAt: new Date().toISOString(),
    root: createFrameNode('Hero shell', {
      maxWidth: '100%',
      minHeight: '34rem',
      padding: '2rem',
      borderRadius: '2rem',
      backgroundImage: 'linear-gradient(135deg, rgba(37, 99, 235, 0.12), rgba(15, 118, 110, 0.08))',
      borderColor: 'rgba(37, 99, 235, 0.16)',
      boxShadow: '0 1.5rem 3.5rem rgba(15, 23, 42, 0.08)'
    }, [
      createStackNode('Hero columns', {
        flexDirection: 'row',
        gap: '1.5rem',
        alignItems: 'stretch'
      }, [
        createStackNode('Copy column', {
          justifyContent: 'center',
          alignItems: 'flex-start',
          gap: '1rem',
          width: 'minmax(0, 1fr)',
          maxWidth: '32rem'
        }, [
          createBadgeNode('Announcement', {
            badgeText: 'Launch faster with reusable sections'
          }),
          createTextNode('Hero copy', {
            eyebrow: 'Component system',
            title: 'Build polished website blocks once and reuse them across every client project.',
            text: 'Design a component, map its editable schema, and ship consistent layouts without copying styles by hand.',
            secondaryText: 'Use presets, variants, and drag-and-drop layering to keep delivery fast.'
          }, {
            fontSize: '1rem',
            lineHeight: '1.7'
          }),
          createStackNode('CTA row', {
            flexDirection: 'row',
            gap: '0.85rem',
            alignItems: 'center'
          }, [
            createButtonNode('Primary CTA', {
              buttonText: 'Start building'
            }, {
              backgroundColor: '#2563eb',
              color: '#ffffff',
              borderColor: '#2563eb'
            }),
            createButtonNode('Secondary CTA', {
              buttonText: 'Preview library'
            }, {
              backgroundColor: 'rgba(15, 23, 42, 0.04)',
              color: '#0f172a',
              borderColor: 'rgba(15, 23, 42, 0.12)'
            })
          ])
        ]),
        createMediaNode('Proof mockup', {
          mediaLabel: 'Live component preview',
          caption: 'Drag blocks, change styles, and keep a single source of truth.'
        }, {
          minHeight: '22rem',
          backgroundImage: 'linear-gradient(145deg, rgba(15, 23, 42, 0.96), rgba(37, 99, 235, 0.78))',
          color: '#ffffff',
          borderColor: 'rgba(255, 255, 255, 0.15)',
          boxShadow: '0 1.25rem 2.8rem rgba(15, 23, 42, 0.2)'
        })
      ])
    ])
  };
}

function createPricingMatrixDefinition(): StudioComponentDefinition {
  return {
    id: createStudioId('component'),
    slug: 'pricing-matrix',
    name: 'Pricing matrix',
    source: 'built-in',
    category: 'commerce',
    description: 'A pricing comparison surface with highlighted plan cards and feature breakdown.',
    tags: ['pricing', 'plans', 'cards'],
    version: 2,
    status: 'draft',
    usageCount: 7,
    updatedAt: new Date().toISOString(),
    root: createFrameNode('Pricing shell', {
      padding: '1.5rem',
      backgroundColor: 'rgba(15, 23, 42, 0.03)',
      borderRadius: '1.75rem'
    }, [
      createTextNode('Pricing intro', {
        eyebrow: 'Plans',
        title: 'Choose the workflow that matches your build volume.',
        text: 'Scale from a single in-progress website to large client inventories without rebuilding the stack.'
      }, {
        margin: '0 0 1rem'
      }),
      createStackNode('Pricing cards', {
        flexDirection: 'row',
        gap: '1rem'
      }, [
        createMediaNode('Starter card', {
          mediaLabel: 'Free',
          caption: '1 site draft, builder access, publish locked'
        }, {
          minHeight: '16rem',
          backgroundColor: '#ffffff'
        }),
        createMediaNode('Builder card', {
          mediaLabel: 'Builder',
          caption: '25 active sites, templates, custom domains'
        }, {
          minHeight: '16rem',
          backgroundImage: 'linear-gradient(135deg, rgba(37, 99, 235, 0.14), rgba(124, 58, 237, 0.12))'
        }),
        createMediaNode('Agency card', {
          mediaLabel: 'Pro Builder',
          caption: '75 active sites, priority support, future features'
        }, {
          minHeight: '16rem',
          backgroundColor: '#ffffff'
        })
      ])
    ])
  };
}

function createSocialProofDefinition(): StudioComponentDefinition {
  return {
    id: createStudioId('component'),
    slug: 'social-proof-strip',
    name: 'Social proof strip',
    source: 'built-in',
    category: 'content',
    description: 'A compact testimonial and trust-strip component for service business homepages.',
    tags: ['testimonial', 'trust', 'reviews'],
    version: 3,
    status: 'ready',
    usageCount: 11,
    updatedAt: new Date().toISOString(),
    root: createFrameNode('Proof shell', {
      padding: '1.5rem',
      backgroundColor: '#ffffff',
      borderRadius: '1.5rem',
      boxShadow: '0 1rem 2rem rgba(15, 23, 42, 0.06)'
    }, [
      createStackNode('Proof row', {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem'
      }, [
        createTextNode('Testimonial copy', {
          eyebrow: 'Trusted by teams',
          title: '“We cut delivery time in half once our component system stopped living in random files.”',
          text: 'Used by agencies building high-volume landing pages and reusable campaign assets.'
        }, {
          maxWidth: '30rem'
        }),
        createStackNode('Logo badges', {
          flexDirection: 'row',
          gap: '0.75rem',
          alignItems: 'center'
        }, [
          createBadgeNode('Agency badge', {
            badgeText: 'Agency flow'
          }),
          createBadgeNode('Speed badge', {
            badgeText: 'Faster delivery'
          }),
          createBadgeNode('Trust badge', {
            badgeText: 'Reusable system'
          })
        ])
      ])
    ])
  };
}

function createBlankComponentDefinition(name: string): StudioComponentDefinition {
  return {
    id: createStudioId('component'),
    slug: slugify(name),
    name,
    source: 'custom',
    category: 'utility',
    description: 'A fresh reusable component ready for your own structure, style system, and editable schema.',
    tags: ['new'],
    version: 1,
    status: 'draft',
    usageCount: 0,
    updatedAt: new Date().toISOString(),
    root: createFrameNode('Component shell', {
      minHeight: '22rem',
      padding: '1.5rem',
      backgroundColor: '#ffffff',
      borderRadius: '1.5rem'
    }, [
      createTextNode('Intro copy', {
        eyebrow: 'New component',
        title: 'Start structuring your reusable block.',
        text: 'Insert frame, stack, text, button, media, and badge primitives from the preset panel.'
      })
    ])
  };
}

const BUILT_IN_COMPONENT_SLUGS = new Set([
  'hero-spotlight',
  'pricing-matrix',
  'social-proof-strip'
]);

function normalizeDefinition(definition: StudioComponentDefinition): StudioComponentDefinition {
  return {
    ...definition,
    source: definition.source ?? (BUILT_IN_COMPONENT_SLUGS.has(definition.slug) ? 'built-in' : 'custom'),
    root: normalizeStudioNodeTree(definition.root)
  };
}

function cloneNodeWithFreshIds(node: StudioNode): StudioNode {
  const draftNode = structuredClone(node);
  walkAndRefreshIds(draftNode);
  return draftNode;
}

function walkAndRefreshIds(node: StudioNode): void {
  node.id = createStudioId('node');
  node.children.forEach((child) => walkAndRefreshIds(child));
}

function buildNodeFromPreset(presetId: string): StudioNode {
  switch (presetId) {
    case 'button-row':
      return createStackNode('Button group', {
        flexDirection: 'row',
        gap: '0.75rem',
        alignItems: 'center'
      }, [
        createButtonNode('Primary action', {
          buttonText: 'Primary action'
        }, {
          backgroundColor: '#2563eb',
          color: '#ffffff',
          borderColor: '#2563eb'
        }),
        createButtonNode('Secondary action', {
          buttonText: 'Secondary action'
        }, {
          backgroundColor: 'rgba(15, 23, 42, 0.04)',
          borderColor: 'rgba(15, 23, 42, 0.16)'
        })
      ]);
    case 'media-card':
      return createMediaNode('Media container', {
        mediaLabel: 'Media preview',
        caption: 'Use this node for screenshots, device mockups, or branded visuals.'
      }, {
        minHeight: '16rem',
        backgroundImage: 'linear-gradient(135deg, rgba(37, 99, 235, 0.16), rgba(124, 58, 237, 0.1))'
      });
    case 'feature-stack':
      return createStackNode('Stack container', {
        gap: '0.75rem',
        padding: '1rem',
        backgroundColor: 'rgba(15, 23, 42, 0.03)',
        borderRadius: '1rem',
        borderColor: 'rgba(15, 23, 42, 0.08)'
      }, [
        createTextNode('Feature headline', {
          title: 'Feature highlight',
          text: 'Explain the value, supporting behavior, or conversion outcome for this block.'
        }),
        createBadgeNode('Feature badge', {
          badgeText: 'Ready for editing'
        })
      ]);
    case 'badge-cluster':
      return createStackNode('Badge group', {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: '0.65rem',
        alignItems: 'center'
      }, [
        createBadgeNode('Badge one', {
          badgeText: 'Fast deploy'
        }),
        createBadgeNode('Badge two', {
          badgeText: 'Reusable'
        }),
        createBadgeNode('Badge three', {
          badgeText: 'Client ready'
        })
      ]);
    case 'text-block':
    default:
      return createTextNode('Text node block', {
        eyebrow: 'Text primitive',
        title: 'A reusable heading and paragraph node set.',
        text: 'Use the inspector to shape typography, spacing, and optional advanced CSS rules.'
      });
  }
}

function createFrameNode(
  label: string,
  stylePatch: Partial<StudioNodeStyle> = {},
  children: StudioNode[] = []
): StudioNode {
  return {
    id: createStudioId('node'),
    type: 'frame',
    label,
    hidden: false,
    locked: false,
    content: {},
    style: {
      ...createBaseStyle(),
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      width: '100%',
      maxWidth: '72rem',
      minHeight: '18rem',
      padding: '1.5rem',
      backgroundColor: '#ffffff',
      borderRadius: '1.5rem',
      ...stylePatch
    },
    children
  };
}

function createStackNode(
  label: string,
  stylePatch: Partial<StudioNodeStyle> = {},
  children: StudioNode[] = []
): StudioNode {
  return {
    id: createStudioId('node'),
    type: 'stack',
    label,
    hidden: false,
    locked: false,
    content: {},
    style: {
      ...createBaseStyle(),
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      width: '100%',
      padding: '0',
      borderWidth: '0',
      backgroundColor: 'transparent',
      ...stylePatch
    },
    children
  };
}

function createTextNode(
  label: string,
  content: StudioNodeContent,
  stylePatch: Partial<StudioNodeStyle> = {}
): StudioNode {
  return {
    id: createStudioId('node'),
    type: 'text',
    label,
    hidden: false,
    locked: false,
    content,
    style: {
      ...createBaseStyle(),
      gap: '0.5rem',
      padding: '0',
      backgroundColor: 'transparent',
      borderWidth: '0',
      boxShadow: 'none',
      ...stylePatch
    },
    children: []
  };
}

function createButtonNode(
  label: string,
  content: StudioNodeContent,
  stylePatch: Partial<StudioNodeStyle> = {}
): StudioNode {
  return {
    id: createStudioId('node'),
    type: 'button',
    label,
    hidden: false,
    locked: false,
    content,
    style: {
      ...createBaseStyle(),
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: 'auto',
      padding: '0.85rem 1.2rem',
      borderRadius: '999px',
      backgroundColor: '#ffffff',
      fontWeight: '700',
      ...stylePatch
    },
    children: []
  };
}

function createMediaNode(
  label: string,
  content: StudioNodeContent,
  stylePatch: Partial<StudioNodeStyle> = {}
): StudioNode {
  return {
    id: createStudioId('node'),
    type: 'media',
    label,
    hidden: false,
    locked: false,
    content,
    style: {
      ...createBaseStyle(),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      minHeight: '14rem',
      padding: '1.15rem',
      backgroundColor: 'rgba(15, 23, 42, 0.06)',
      borderRadius: '1.25rem',
      ...stylePatch
    },
    children: []
  };
}

function createBadgeNode(
  label: string,
  content: StudioNodeContent,
  stylePatch: Partial<StudioNodeStyle> = {}
): StudioNode {
  return {
    id: createStudioId('node'),
    type: 'badge',
    label,
    hidden: false,
    locked: false,
    content,
    style: {
      ...createBaseStyle(),
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: 'auto',
      padding: '0.45rem 0.8rem',
      borderRadius: '999px',
      backgroundColor: 'rgba(37, 99, 235, 0.12)',
      color: '#1d4ed8',
      borderColor: 'rgba(37, 99, 235, 0.18)',
      borderWidth: '1px',
      fontSize: '0.82rem',
      fontWeight: '700',
      ...stylePatch
    },
    children: []
  };
}

function createBaseStyle(): StudioNodeStyle {
  return createDefaultStudioNodeStyle();
}

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'component';
}
